import { useEffect } from 'react';

// Canonical production origin — used to build absolute URLs for canonical/OG tags.
export const SITE_URL = 'https://www.zeerowbalance.com';

// Person schema — powers Google's knowledge-panel / rich name results.
export const PERSON_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Himanshu Kumar Tanty',
    alternateName: 'Himanshu Tanty',
    url: SITE_URL,
    image: `${SITE_URL}/og-cover.png`,
    jobTitle: 'Senior Product Design Leader',
    description:
        'Senior product designer with over a decade of experience across enterprise, AI and SaaS, turning complex workflows into clear, scalable products.',
    worksFor: { '@type': 'Organization', name: 'Wipro' },
    knowsAbout: [
        'Enterprise UX',
        'Product Design',
        'AI Product Design',
        'SaaS Design',
        'Design Systems',
        'UI/UX Design',
    ],
    sameAs: [
        'https://www.linkedin.com/in/anshuu/',
        'https://www.instagram.com/anshuhim/',
    ],
};

// WebSite schema — associates the site with its author.
export const WEBSITE_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Zeerow Balance · Himanshu Tanty',
    url: SITE_URL,
    author: { '@type': 'Person', name: 'Himanshu Kumar Tanty' },
};

// CreativeWork schema for a single case study — richer search results per project.
export const caseStudySchema = (cs, slug) => ({
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: cs.title,
    ...(cs.subtitle ? { headline: cs.subtitle } : {}),
    ...(cs.overview || cs.note
        ? { description: clampDescription(cs.overview || cs.note, 300) }
        : {}),
    ...(cs.category ? { genre: cs.category } : {}),
    url: `${SITE_URL}/work/${slug}`,
    ...(cs.cover ? { image: `${SITE_URL}${cs.cover}` } : {}),
    creator: { '@type': 'Person', name: 'Himanshu Kumar Tanty', url: SITE_URL },
    author: { '@type': 'Person', name: 'Himanshu Kumar Tanty' },
});


// Trim a string to a clean ~155-char meta description without cutting mid-word.
export const clampDescription = (text, max = 155) => {
    if (!text) return '';
    const clean = text.replace(/\s+/g, ' ').trim();
    if (clean.length <= max) return clean;
    const cut = clean.slice(0, max);
    return cut.slice(0, cut.lastIndexOf(' ')).trim() + '…';
};

const upsert = (tag, keyAttr, keyVal) => {
    let el = document.head.querySelector(`${tag}[${keyAttr}="${keyVal}"]`);
    let created = false;
    if (!el) {
        el = document.createElement(tag);
        el.setAttribute(keyAttr, keyVal);
        document.head.appendChild(el);
        created = true;
    }
    return { el, created };
};

/**
 * Seo — imperatively sets per-page <title>, description, canonical and OG/Twitter
 * tags, then restores the previous values on unmount. Replaces (never duplicates)
 * the static tags in index.html, so each route ranks on its own keywords.
 * React 19-safe (no react-helmet, which doesn't support React 19).
 */
export const Seo = ({ title, description, canonical, image, jsonLd }) => {
    useEffect(() => {
        const restores = [];

        // JSON-LD structured data — inject one <script> per schema so Google can
        // surface rich results (Person, CreativeWork/Review). Removed on unmount.
        if (jsonLd) {
            const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
            schemas.forEach((schema) => {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.setAttribute('data-seo-jsonld', '');
                script.textContent = JSON.stringify(schema);
                document.head.appendChild(script);
                restores.push(() => script.remove());
            });
        }

        if (title) {
            const prev = document.title;
            document.title = title;
            restores.push(() => {
                document.title = prev;
            });
        }

        const applyMeta = (attr, key, content) => {
            if (!content) return;
            const { el, created } = upsert('meta', attr, key);
            const prev = el.getAttribute('content');
            el.setAttribute('content', content);
            restores.push(() => {
                if (created) el.remove();
                else if (prev != null) el.setAttribute('content', prev);
            });
        };

        applyMeta('name', 'description', description);
        applyMeta('property', 'og:title', title);
        applyMeta('property', 'og:description', description);
        applyMeta('property', 'og:url', canonical);
        applyMeta('name', 'twitter:title', title);
        applyMeta('name', 'twitter:description', description);
        if (image) applyMeta('property', 'og:image', image);

        if (canonical) {
            const { el, created } = upsert('link', 'rel', 'canonical');
            const prev = el.getAttribute('href');
            el.setAttribute('href', canonical);
            restores.push(() => {
                if (created) el.remove();
                else if (prev != null) el.setAttribute('href', prev);
            });
        }

        return () => restores.reverse().forEach((fn) => fn());
    }, [title, description, canonical, image, jsonLd]);

    return null;
};

export default Seo;
